/**
 * When the onboarding Camera Feeds step counts as done — the rule behind its green tick.
 *
 * ## Why it moved out of `OnboardingPage`
 *
 * It was two lines in the page body, and `camera-observer-setup-stream-first` had to change the
 * rule itself:
 *
 *     const feedsComplete = cameras.length > 0 && linkedCameras === cameras.length;
 *
 * `OnboardingPage.tsx` is ~2,900 lines and pulls in Auth0, four stores, the planner and
 * the 3D plan, so mounting it to assert a predicate characterises the mocks rather than the rule.
 * Lifting it out is the smallest change that makes it testable for real — the same move, for the
 * same reason, that `registerCompletion.ts` made for the Equipment step.
 *
 * ## The rule, and what changed
 *
 * `cameras.length > 0` made **placing a camera a precondition for the tick**, which is the thing
 * this epic exists to remove. On staging it could never be satisfied: the only two twins with a
 * saved plan hold zero cameras between them, against 14 and 8 live streams. Production's six saved
 * plans are the same shape.
 *
 * The tick now means "this step has nothing outstanding", which is three claims:
 *
 *  1. **The streams were actually read.** A tick is an affirmative claim, and a failed read
 *     establishes nothing — so it is grey, not green. This is the term the old rule had no way to
 *     express, because it never looked at the read at all.
 *  2. **The twin has at least one stream.** A twin with none has nothing for an observer to watch,
 *     so the step is unstarted rather than complete.
 *  3. **No camera on the plan is left without a stream.** The half of the old rule that was right:
 *     a camera someone placed and never linked is unfinished work, and placing one is still
 *     optional.
 */

/** The subset of a plan camera this rule reads. */
export interface FeedsCompletionCamera {
  rtspEndpointId?: number | null;
}

/** The subset of the page's stream read this rule reads. */
export type FeedsCompletionStreams =
  | { status: 'loading' }
  | { status: 'ready'; value: unknown[] }
  | { status: 'failed' };

export const feedsComplete = (
  streams: FeedsCompletionStreams | undefined,
  cameras: FeedsCompletionCamera[],
): boolean => {
  // Term 1. `undefined` is "not read yet", which is not the same as "failed" and is equally not a
  // basis for a tick.
  if (streams?.status !== 'ready') return false;
  // Term 2.
  if (streams.value.length === 0) return false;
  // Term 3.
  return cameras.every(c => c.rtspEndpointId != null);
};
